import { useEffect, useRef, useState, useCallback } from 'react';
import { logger } from './lib/logger';
import supabase from './supabase';

// Deterministic color from userId
function userColor(userId) {
  const COLORS = [
    '#ef4444', '#f97316', '#eab308', '#22c55e',
    '#14b8a6', '#3b82f6', '#8b5cf6', '#ec4899',
  ];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return COLORS[Math.abs(hash) % COLORS.length];
}

// Single payload builder — one place to add/remove DB fields.
//
// gantt_start_date is deliberately OMITTED from the returned object (rather than
// defaulted to null) whenever the caller doesn't pass it at all — supabase-js's
// .update() only touches keys actually present in the object, so omitting it
// leaves the column untouched. A caller that forgets to pass ganttStartDate is
// exactly what used to silently wipe it back to null on every single regular
// save (see FlowchartBuilder.tsx's useFlowchartSync() call — ganttStartDate must
// be forwarded there), re-triggering the exact "null gantt_start_date floats to
// today" bug the one-time backfill + creation-path fixes were meant to prevent
// for good. An explicit `null`/`undefined` VALUE (as opposed to omitting the key
// entirely) still clears it, in case that's ever genuinely wanted.
function buildPayload(data) {
  const payload = {
    title: data.title,
    boxes: data.boxes ?? [],
    arrows: data.arrows ?? [],
    departments: data.departments ?? [],
    areas: data.areas ? JSON.stringify(data.areas) : null,
    resources: data.resources ?? [],
    duration_unit: data.durationUnit ?? 'days',
    updated_by_email: data.updatedByEmail ?? null,
  };
  if ('ganttStartDate' in data) {
    payload.gantt_start_date = data.ganttStartDate
      ? (data.ganttStartDate instanceof Date
          ? data.ganttStartDate.toISOString().split('T')[0]
          : data.ganttStartDate)
      : null;
  }
  return payload;
}

/**
 * Custom hook for realtime sync of flowcharts.
 * Automatically syncs changes between users working on the same flowchart.
 */
export function useRealtimeSync(flowchartId, userId, options = {}) {
  const { onUpdate, onError, enabled = true } = options;
  const channelRef = useRef(null);
  const lastUpdateRef = useRef(null);

  // updated_at timestamps (ms) of our own writes. Realtime events with such a timestamp
  // are our own echo and must not overwrite local state. This is exact and
  // independent of JSON key order (jsonb reorders keys, which makes
  // content comparison structurally unreliable as echo detection).
  const ownWritesRef = useRef(new Set());

  const registerOwnWrite = useCallback((updatedAt) => {
    if (!updatedAt) return;
    const t = new Date(updatedAt).getTime();
    if (isNaN(t)) return;
    ownWritesRef.current.add(t);
    // Limit memory usage: only the most recent writes are relevant
    if (ownWritesRef.current.size > 20) {
      const first = ownWritesRef.current.values().next().value;
      ownWritesRef.current.delete(first);
    }
  }, []);

  // Refs keep the subscription stable across renders
  const onUpdateRef = useRef(onUpdate);
  const onErrorRef = useRef(onError);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  useEffect(() => {
    if (!flowchartId || !userId || !enabled) return;

    logger.log('🔄 Setting up realtime sync for flowchart:', flowchartId);

    const parseField = (value, fallback = []) => {
      if (!value) return fallback;
      if (typeof value === 'string') {
        try { return JSON.parse(value); } catch { return fallback; }
      }
      return Array.isArray(value) ? value : fallback;
    };

    const channel = supabase
      .channel(`flowchart:${flowchartId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'flowcharts', filter: `id=eq.${flowchartId}` },
        (payload) => {
          logger.log('📥 Received update from database:', payload);

          const updateTime = new Date(payload.new.updated_at);
          if (ownWritesRef.current.has(updateTime.getTime())) {
            logger.log('⏭️  Ignoring own write echo (updated_at match)');
            return;
          }
          if (lastUpdateRef.current && updateTime.getTime() === lastUpdateRef.current.getTime()) {
            logger.log('⏭️  Ignoring duplicate update (same timestamp)');
            return;
          }

          try {
            const data = {
              ...payload.new,
              boxes: parseField(payload.new.boxes),
              arrows: parseField(payload.new.arrows),
              departments: parseField(payload.new.departments),
              areas: parseField(payload.new.areas),
            };

            logger.log('✅ Parsed update data:', data);
            lastUpdateRef.current = updateTime;
            onUpdateRef.current?.(data);
          } catch (error) {
            logger.error('❌ Error parsing realtime update:', error);
            onErrorRef.current?.(error);
          }
        }
      )
      .subscribe((status) => {
        logger.log('📡 Realtime subscription status:', status);
        if (status === 'CHANNEL_ERROR') {
          logger.error('❌ Realtime subscription error');
          onErrorRef.current?.(new Error('Realtime subscription failed'));
        }
      });

    channelRef.current = channel;

    return () => {
      logger.log('🔌 Cleaning up realtime subscription');
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  // onUpdate/onError are intentionally omitted — tracked via refs instead
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowchartId, userId, enabled]);

  // expectedVersion (optional): optimistic locking. The update only affects the row
  // if the version in the DB still matches what this client last saw.
  // 0 rows affected = another user wrote first → { conflict: true }, so
  // the caller can refetch and merge instead of silently overwriting their work.
  const syncToDatabase = useCallback(async (data, expectedVersion) => {
    if (!flowchartId) {
      logger.warn('⚠️  Cannot sync: no flowchart ID');
      return { success: false, error: 'No flowchart ID' };
    }

    try {
      logger.log('💾 Syncing to database...', flowchartId);
      let query = supabase
        .from('flowcharts')
        .update(buildPayload(data))
        .eq('id', flowchartId);
      if (expectedVersion !== undefined && expectedVersion !== null) {
        query = query.eq('version', expectedVersion);
      }
      const { data: saved, error } = await query.select('updated_at, version').maybeSingle();

      if (error) {
        logger.error('❌ Sync error:', error);
        onErrorRef.current?.(error);
        return { success: false, error };
      }
      if (!saved) {
        logger.warn('⚠️  Sync conflict: version mismatch (expected', expectedVersion, ')');
        return { success: false, conflict: true };
      }
      logger.log('✅ Synced successfully');
      registerOwnWrite(saved.updated_at);
      return { success: true, updatedAt: saved.updated_at ?? null, version: saved.version ?? null };
    } catch (error) {
      logger.error('❌ Sync exception:', error);
      onErrorRef.current?.(error);
      return { success: false, error };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowchartId]);

  return { syncToDatabase, registerOwnWrite, isSubscribed: channelRef.current !== null };
}

// ── Presence ────────────────────────────────────────────────────────────────

export function usePresence(flowchartId, user, enabled = true) {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const channelRef = useRef(null);
  const throttleRef = useRef(null);
  // Cursor positions live in a ref + a manual listener set, NOT useState — a
  // dragging user broadcasts their position on every mousemove (throttled to
  // ~30/s), and with several people actively editing at once, running that
  // through setState here would re-render the CALLER of usePresence (the main
  // FlowchartBuilder component, with 100+ boxes/arrows in its tree) up to
  // dozens of times a second per other active user — pure main-thread
  // contention with whatever the local user is themselves dragging. Only
  // PresenceLayer actually needs to re-render on a cursor update; it
  // subscribes directly via subscribeCursors and holds its own local state,
  // isolating that re-render to its own small subtree.
  const cursorsRef = useRef({});
  const cursorListenersRef = useRef(new Set());
  const notifyCursorListeners = useCallback(() => {
    cursorListenersRef.current.forEach(listener => listener(cursorsRef.current));
  }, []);

  useEffect(() => {
    if (!flowchartId || !user?.id || !enabled) {
      setOnlineUsers([]);
      cursorsRef.current = {};
      notifyCursorListeners();
      return;
    }

    const channelName = `presence:${flowchartId}`;
    let cancelled = false;
    let reconnectTimer = null;
    let currentChannel = null;
    // Consecutive CHANNEL_ERROR/TIMED_OUT/CLOSED failures since the last successful
    // SUBSCRIBED — drives the backoff below and when to stop retrying altogether.
    // A fixed 2s retry with no ceiling, under a sustained connectivity problem (not
    // just a one-off blip), hammers the socket forever: each cycle's removeChannel()
    // is async, so a tight retry loop can start the next channel before the previous
    // one's listeners are fully detached, and that overlap is what surfaced as a
    // real "Maximum call stack size exceeded" in realtime-js's Push/event-trigger
    // machinery (not a bug in this app's code directly, but provoked by retrying
    // this aggressively against a socket that isn't recovering).
    let consecutiveFailures = 0;
    const MAX_RECONNECT_ATTEMPTS = 8;
    // Set once this exact channel instance has been given up on (see the
    // CHANNEL_ERROR/TIMED_OUT/CLOSED branch below). The channel!==currentChannel
    // guard doesn't cover this case — currentChannel keeps pointing at this same,
    // already-given-up channel, since giving up deliberately doesn't schedule a
    // replacement. Without this flag, the underlying socket continuing to emit
    // MORE status events for an already-removed channel (confirmed live: the same
    // "giving up" log line repeating 600+ times in a tight burst) re-ran the whole
    // removeChannel()/log block every single time — which is exactly what was
    // crashing with a real "Maximum call stack size exceeded" in realtime-js's own
    // listener-trigger machinery, and almost certainly what froze the tab's main
    // thread (hundreds of synchronous iterations with no delay between them).
    let gaveUp = false;

    // Extracted so it can run again after a broken channel or a tab waking from
    // suspend — see both call sites below for why a channel that goes bad here
    // previously had no way to recover on its own.
    const setupChannel = () => {
      if (cancelled) return;
      gaveUp = false;

      const channel = supabase.channel(channelName, {
        config: { presence: { key: user.id } },
      });

      channel.on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const users = Object.entries(state).map(([uid, presences]) => ({
          userId: uid,
          email: presences[0]?.email ?? uid,
          color: userColor(uid),
        }));
        logger.log('👥 presence sync', channelName, 'raw state:', state, 'derived users (excl. self):', users.filter(u => u.userId !== user.id).length);
        setOnlineUsers(users.filter(u => u.userId !== user.id));
      });

      channel.on('broadcast', { event: 'cursor' }, ({ payload }) => {
        if (payload.userId === user.id) return;
        if (!(payload.userId in cursorsRef.current)) {
          logger.log('👥 first cursor broadcast received from', payload.userId, channelName);
        }
        cursorsRef.current = {
          ...cursorsRef.current,
          [payload.userId]: {
            userId: payload.userId,
            email: payload.email,
            color: userColor(payload.userId),
            x: payload.x,
            y: payload.y,
            lastSeen: Date.now(),
          },
        };
        notifyCursorListeners();
      });

      channel.on('broadcast', { event: 'leave' }, ({ payload }) => {
        const next = { ...cursorsRef.current };
        delete next[payload.userId];
        cursorsRef.current = next;
        notifyCursorListeners();
        setOnlineUsers(prev => prev.filter(u => u.userId !== payload.userId));
      });

      channel.subscribe(async (status) => {
        logger.log('👥 Presence channel status:', status, channelName);
        // A status callback can still fire after this exact channel instance has
        // already been superseded (removeChannel's own teardown, or a newer
        // setupChannel() call from onVisibilityChange, is async) — acting on it
        // anyway used to be able to spawn a SECOND, redundant reconnect cycle
        // stacking on top of whatever superseded it in the first place, each with
        // its own live event listeners left attached. Bail out once this channel
        // is no longer the current one.
        if (cancelled || channel !== currentChannel || gaveUp) return;
        if (status === 'SUBSCRIBED') {
          consecutiveFailures = 0;
          const trackResult = await channel.track({ email: user.email ?? user.id });
          logger.log('👥 Track result:', trackResult);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          // A channel in one of these states does not recover on its own — previously
          // this just logged and left onlineUsers/cursorsRef frozen at whatever they
          // last were, which can go stale in EITHER direction: a participant who left
          // while this channel was dead still shows as online (their 'leave' broadcast
          // never arrived), or one who joined never appears (their presence was never
          // synced in). Tear down and rebuild after a short delay instead.
          consecutiveFailures++;
          supabase.removeChannel(channel);
          if (reconnectTimer) clearTimeout(reconnectTimer);
          if (consecutiveFailures > MAX_RECONNECT_ATTEMPTS) {
            // The socket isn't recovering (dead network, revoked/expired session, ...) —
            // retrying forever only makes things worse (see comment above). Give up
            // automatically; presence/cursors for this flowchart go stale rather than
            // the tab grinding on an endless reconnect storm. A real tab reload (or,
            // if this is actually caused by an expired session, the sessionExpiredElsewhere
            // sign-out flow in App.tsx) is what recovers from here, not another retry.
            // gaveUp=true makes this a one-time log: without it, the underlying socket
            // continuing to fire more status events for this same (already removed)
            // channel re-ran this entire branch — including another removeChannel() call
            // on a channel already being torn down — every single time, hundreds of
            // times in a tight burst, which is what actually crashed/froze the tab.
            gaveUp = true;
            logger.error('👥 Presence channel unusable, giving up after', consecutiveFailures, 'consecutive failures:', status, channelName);
            return;
          }
          const delay = Math.min(2000 * 2 ** (consecutiveFailures - 1), 30000);
          logger.error('👥 Presence channel unusable, reconnecting in', delay, 'ms (attempt', consecutiveFailures, '):', status, channelName);
          reconnectTimer = setTimeout(setupChannel, delay);
        }
      });

      currentChannel = channel;
      channelRef.current = channel;
    };

    setupChannel();

    // A laptop going to sleep freezes this tab's JS (timers, and often the socket
    // itself) for the whole duration — any presence join/leave broadcast for other
    // participants during that window is simply never received, and the channel may
    // not visibly error out even though its view of who's online is now stale. Force
    // a full rebuild after a REAL absence rather than trusting whatever state the
    // existing channel resumes in; a fresh subscribe re-syncs the authoritative
    // presence state from Realtime regardless of what was missed.
    //
    // Gated on actually having been hidden for a while (not just "became visible")
    // — visibilitychange also fires on every ordinary alt-tab, and rebuilding the
    // channel unconditionally on each one raced against this same channel's own
    // in-flight subscribe/reconnect cycle (nothing here cancelled a pending
    // reconnectTimer before calling setupChannel again), which could leave more
    // than one channel instance alive with live listeners at once — each
    // subsequent quick tab-switch compounding it further. A user reported exactly
    // this shape of bug (page eventually unresponsive after repeated pan/zoom +
    // switch-tabs-and-back cycles).
    let hiddenAt = null;
    const HIDDEN_REBUILD_THRESHOLD_MS = 10000;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (document.visibilityState !== 'visible' || !currentChannel) return;
      const wasHiddenLongEnough = hiddenAt !== null && (Date.now() - hiddenAt) >= HIDDEN_REBUILD_THRESHOLD_MS;
      hiddenAt = null;
      if (!wasHiddenLongEnough) return;
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      supabase.removeChannel(currentChannel);
      // Returning to the tab after a real absence is a natural point to give the
      // connection a fresh full retry budget, including after the give-up ceiling
      // above was hit while the tab was hidden.
      consecutiveFailures = 0;
      setupChannel();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (currentChannel) {
        currentChannel.send({ type: 'broadcast', event: 'leave', payload: { userId: user.id } });
        supabase.removeChannel(currentChannel);
      }
      if (throttleRef.current) clearTimeout(throttleRef.current);
    };
  }, [flowchartId, user?.id, user?.email, enabled, notifyCursorListeners]);

  const broadcastCursor = useCallback((canvasX, canvasY) => {
    if (!channelRef.current || !user) return;
    if (throttleRef.current) return;
    throttleRef.current = setTimeout(() => { throttleRef.current = null; }, 33);
    channelRef.current.send({
      type: 'broadcast',
      event: 'cursor',
      payload: { userId: user.id, email: user.email ?? user.id, x: canvasX, y: canvasY },
    });
  }, [user]);

  // Registers `listener` to be called with the current cursor map on every
  // update (and once immediately, to seed it). Returns an unsubscribe function.
  const subscribeCursors = useCallback((listener) => {
    listener(cursorsRef.current);
    cursorListenersRef.current.add(listener);
    return () => cursorListenersRef.current.delete(listener);
  }, []);

  return { onlineUsers, broadcastCursor, subscribeCursors };
}

export { userColor };
